/**
 * FIREBASE ORDER SERVICE - Complete Django Replacement
 * All order operations now handled by Firebase Firestore
 */

export interface FirebaseOrder {
  id?: string;
  order_number: string;
  customer_name: string;
  order_type: 'dine_in' | 'takeaway' | 'delivery';
  payment_method: 'cash' | 'card' | 'paypal' | 'apple_pay' | 'google_pay';
  payment_status: 'pending' | 'completed' | 'failed' | 'refunded';
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'delivered' | 'completed' | 'cancelled';
  subtotal: number;
  tax: number;
  total: number;
  notes?: string;
  items: FirebaseOrderItem[];
  table_number?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  branch?: string;
  // Split payment fields
  is_split_payment?: boolean;
  split_session_id?: string;
  split_person_name?: string;
  split_total_people?: number;
  split_person_index?: number;
}

export interface FirebaseOrderItem {
  id?: string;
  menu_item_id: string;
  menu_item_name: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total: number;
  customizations?: any;
  notes?: string;
}

// Session-level cache so we fetch user info only once per login, not per order
let _cachedUserInfo: { name: string; email: string; uid: string } | null = null;

class FirebaseOrderService {
  private static instance: FirebaseOrderService;
  private firestoreUrl = '__SAHEEH_FIRESTORE_REST_URL_UNSET__';

  private constructor() {
    console.log('🔥 Firebase Order Service initialized - NO MORE DJANGO!');
  }

  public static getInstance(): FirebaseOrderService {
    if (!FirebaseOrderService.instance) {
      FirebaseOrderService.instance = new FirebaseOrderService();
    }
    return FirebaseOrderService.instance;
  }

  /**
   * Get next sequential order number from Firestore counter
   * Returns 5-digit zero-padded string (00001, 00002, ...)
   */
  public async getNextOrderNumber(): Promise<string> {
    const { doc, runTransaction } = await import('../services/firestoreCompat');
    const { db } = await import('./firebaseConfig');
    const counterRef = doc(db, 'settings', 'order_counter');

    try {
      const newNumber = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);
        let current = 0;
        if (counterDoc.exists()) {
          current = counterDoc.data().current || 0;
        }
        const next = current + 1;
        transaction.set(counterRef, { current: next }, { merge: true });
        return next;
      });
      return String(newNumber).padStart(5, '0');
    } catch (err) {
      console.warn('⚠️ Counter transaction failed, using fallback:', err);
      // Fallback: timestamp-based if counter fails
      const ts = Date.now() % 100000;
      return String(ts).padStart(5, '0');
    }
  }

  /**
   * Pre-allocate multiple order numbers in a single transaction (for split orders).
   * Avoids transaction conflicts when creating orders in parallel.
   */
  public async getNextOrderNumbers(count: number): Promise<string[]> {
    const { doc, runTransaction } = await import('../services/firestoreCompat');
    const { db } = await import('./firebaseConfig');
    const counterRef = doc(db, 'settings', 'order_counter');

    try {
      const numbers = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);
        let current = 0;
        if (counterDoc.exists()) {
          current = counterDoc.data().current || 0;
        }
        const next = current + count;
        transaction.set(counterRef, { current: next }, { merge: true });
        // Return array of allocated numbers
        const result: number[] = [];
        for (let i = 1; i <= count; i++) {
          result.push(current + i);
        }
        return result;
      });
      return numbers.map(n => String(n).padStart(5, '0'));
    } catch (err) {
      console.warn('⚠️ Batch counter transaction failed, using fallback:', err);
      // Fallback: timestamp-based
      const results: string[] = [];
      for (let i = 0; i < count; i++) {
        const ts = (Date.now() + i) % 100000;
        results.push(String(ts).padStart(5, '0'));
      }
      return results;
    }
  }

  /**
   * Create order in Firebase Firestore
   * Uses Firebase SDK instead of REST API to properly handle authentication
   */
  public async createOrder(orderData: any): Promise<FirebaseOrder> {
    try {
      console.log('🔥 [FIREBASE ORDER] Creating order in Firestore...');
      console.log('🔥 [FIREBASE ORDER] STEP 1: Importing Firebase SDK...');
      
      // Use Firebase SDK for proper authentication
      const { collection, addDoc, serverTimestamp } = await import('../services/firestoreCompat');
      const { db, auth } = await import('./firebaseConfig');
      
      // Verify authentication
      console.log('🔥 [FIREBASE ORDER] STEP 2: Checking authentication...');
      if (!auth.currentUser) {
        console.error('❌ [FIREBASE ORDER] No authenticated user found!');
        throw new Error('User not authenticated. Please login again.');
      }
      console.log('✅ [FIREBASE ORDER] Authenticated user:', auth.currentUser.email);
      
      // Get user info (cached per session) + order number in parallel for speed
      const getUserInfo = async () => {
        if (_cachedUserInfo && _cachedUserInfo.uid === auth.currentUser!.uid) {
          return _cachedUserInfo;
        }
        let name = auth.currentUser!.displayName || auth.currentUser!.email || '';
        let email = auth.currentUser!.email || '';
        try {
          const { doc, getDoc } = await import('../services/firestoreCompat');
          const snap = await getDoc(doc(db, 'users', auth.currentUser!.uid));
          if (snap.exists()) {
            const d = snap.data();
            name = d.name || d.first_name || d.displayName || name;
            email = d.email || email;
          }
        } catch { /* use Auth data */ }
        _cachedUserInfo = { name: name || email || 'POS System', email, uid: auth.currentUser!.uid };
        return _cachedUserInfo;
      };

      // Fetch user info and order number in parallel (saves ~300ms per order)
      // If orderData._preAllocatedOrderNumber is set, skip the counter transaction
      const [userInfo, orderNumber] = await Promise.all([
        getUserInfo(),
        orderData._preAllocatedOrderNumber
          ? Promise.resolve(orderData._preAllocatedOrderNumber)
          : this.getNextOrderNumber(),
      ]);

      const createdByUser = userInfo.name;
      const createdByEmail = userInfo.email;
      
      // Prepare Firebase order data - INCLUDING ALL CUSTOMER DETAILS AND SPLIT PAYMENT FIELDS
      const firebaseOrder: any = {
        order_number: orderNumber,
        customer_name: orderData.customer_name || 'Walk-in Customer',
        // CRITICAL: Include all customer detail fields
        customer_first_name: orderData.customer_first_name || '',
        customer_last_name: orderData.customer_last_name || '',
        customer_phone: orderData.customer_phone || '',
        customer_address: orderData.customer_address || '',
        customer_postcode: orderData.customer_postcode || '',
        customer_city: orderData.customer_city || '',
        order_type: orderData.order_type || 'dine_in',
        payment_method: orderData.payment_method || 'cash',
        // CRITICAL FIX: Set payment_status based on payment_method
        // Cash and Card payments are completed immediately (card via swipe machine at POS)
        // Only PayPal payments are pending until payment is captured via QR code
        payment_status: orderData.payment_status || ((orderData.payment_method === 'cash' || orderData.payment_method === 'card') ? 'completed' : 'pending'),
        status: orderData.status || 'confirmed',
        subtotal: parseFloat(orderData.subtotal) || 0,
        tax: parseFloat(orderData.tax) || 0,
        total: parseFloat(orderData.total) || 0,
        notes: orderData.notes || '',
        table_number: orderData.table_number || null,
        table_area: orderData.table_area || null, // CRITICAL: Store area with order to ensure correct table matching
        delivery_address: orderData.delivery_address || null,
        delivery_latitude: orderData.delivery_latitude || null,
        delivery_longitude: orderData.delivery_longitude || null,
        delivery_fee: parseFloat(orderData.delivery_fee) || 0,
        trip_number: orderData.trip_number || null,
        // Discount fields (when applied at checkout)
        discount_amount: parseFloat(orderData.discount_amount) || 0,
        discount_type: orderData.discount_type || '',
        discount_value: parseFloat(orderData.discount_value) || 0,
        discount_reason: orderData.discount_reason || '',
        total_before_discount: parseFloat(orderData.total_before_discount) || 0,
        items: orderData.items.map((item: any, index: number) => {
          // CRITICAL: Ensure we use the correct fields
          const itemName = item.menu_item_name || item.name || 'Unknown Item';
          const itemQuantity = parseInt(item.quantity) || 1;
          const itemUnitPrice = parseFloat(item.unit_price) || parseFloat(item.price) || 0;
          const itemTotal = itemUnitPrice * itemQuantity;
          
          // Log item data being processed
          console.log(`🔥 [FIREBASE ORDER] Processing item ${index + 1}:`, {
            original: {
              menu_item_id: item.menu_item_id,
              menu_item_name: item.menu_item_name,
              name: item.name,
              quantity: item.quantity,
              unit_price: item.unit_price,
              price: item.price,
            },
            processed: {
              name: itemName,
              quantity: itemQuantity,
              unit_price: itemUnitPrice,
              total: itemTotal,
            },
          });
          
          const categoryName = (item.category_name || item.category || '').toLowerCase();
          const itemNameLower = (item.menu_item_name || item.name || '').toLowerCase();
          
          // CRITICAL: Check if item is a drink (always 19% VAT in Germany)
          // Check both category name and item name for comprehensive detection
          // German TSE rules: ALL drinks = 19% VAT regardless of order type (dine-in, takeaway, delivery)
          // and regardless of payment method (cash, card, PayPal)
          const isDrink = categoryName.includes('drink') || 
                          categoryName.includes('getränk') || 
                          categoryName.includes('beverage') ||
                          categoryName.includes('getränke') ||
                          itemNameLower.includes('cola') ||
                          itemNameLower.includes('coca') ||
                          itemNameLower.includes('pepsi') ||
                          itemNameLower.includes('sprite') ||
                          itemNameLower.includes('fanta') ||
                          itemNameLower.includes('wasser') ||
                          itemNameLower.includes('water') ||
                          itemNameLower.includes('saft') ||
                          itemNameLower.includes('juice') ||
                          itemNameLower.includes('limo') ||
                          itemNameLower.includes('softdrink') ||
                          itemNameLower.includes('bier') ||
                          itemNameLower.includes('beer') ||
                          itemNameLower.includes('wein') ||
                          itemNameLower.includes('wine') ||
                          itemNameLower.includes('kaffee') ||
                          itemNameLower.includes('coffee') ||
                          itemNameLower.includes('tee') ||
                          itemNameLower.includes('tea');
          
          // Determine tax rate based on order type and item category
          // CRITICAL GERMAN TSE RULES:
          // - Drinks: ALWAYS 19% VAT (regardless of order type: dine-in, takeaway, delivery)
          // - Drinks: ALWAYS 19% VAT (regardless of payment method: cash, card, PayPal)
          // - Food (Takeaway/Delivery): 7% VAT
          // - Food (Dine-in): 19% VAT
          let taxRate: number;
          if (isDrink) {
            taxRate = 19; // CRITICAL: Drinks ALWAYS 19% VAT in Germany (TSE rule)
            console.log(`🍺 [VAT] ${item.menu_item_name || item.name || 'Item'}: DRINK detected → 19% VAT (German TSE rule)`);
          } else {
            const orderType = orderData.order_type || 'dine_in';
            taxRate = (orderType === 'takeaway' || orderType === 'delivery') ? 7 : 19;
            console.log(`🍱 [VAT] ${item.menu_item_name || item.name || 'Item'}: FOOD (${orderType}) → ${taxRate}% VAT`);
          }
          
          // Calculate base price (without tax) from price_with_tax
          // VAT is included in price, so: basePrice = total / (1 + VAT%)
          // Example: Chicken-Crispy Roll €10.90:
          //   Takeaway (7%): basePrice = 10.90 / 1.07 = 10.19, tax = 10.90 - 10.19 = 0.71
          //   Dine-in (19%): basePrice = 10.90 / 1.19 = 9.16, tax = 10.90 - 9.16 = 1.74
          const basePrice = itemTotal / (1 + taxRate / 100);
          const taxAmount = itemTotal - basePrice;
          
          const firebaseItem = {
            menu_item_id: item.menu_item_id,
            menu_item_name: itemName, // CRITICAL: Use processed name
            name: itemName, // Also include name for compatibility
            quantity: itemQuantity,
            unit_price: itemUnitPrice, // CRITICAL: Use processed unit_price
            price: itemUnitPrice, // Also include price for compatibility
            subtotal: basePrice, // Base price without tax
            tax_rate: taxRate,
            tax_amount: taxAmount,
            total: itemTotal, // Total with tax included
            category_name: item.category_name || item.category || 'Unknown',
            customizations: item.customizations || {},
            notes: item.notes || '',
            addons: item.addons || [], // CRITICAL: Include add-ons in order items
            // Domino's-style modifier selections (Size / Crust / Sauce / Toppings)
            // selections: groupId → [optionId,...]; modifier_snapshot: full groups+options captured at order time
            selections: item.selections || null,
            modifier_snapshot: item.modifier_snapshot || null,
          };
          
          console.log(`✅ [FIREBASE ORDER] Item ${index + 1} saved to Firestore:`, {
            menu_item_name: firebaseItem.menu_item_name,
            quantity: firebaseItem.quantity,
            unit_price: firebaseItem.unit_price,
            total: firebaseItem.total,
            validation: {
              nameIsValid: firebaseItem.menu_item_name !== 'Unknown Item',
              priceIsValid: firebaseItem.unit_price > 0,
              quantityIsValid: firebaseItem.quantity > 0,
              totalMatches: Math.abs(firebaseItem.total - (itemUnitPrice * itemQuantity)) < 0.01,
            },
          });
          
          return firebaseItem;
        }),
        // CRITICAL: Include split payment fields if present
        is_split_payment: orderData.is_split_payment || false,
        split_session_id: orderData.split_session_id || null,
        split_person_name: orderData.split_person_name || null,
        split_total_people: orderData.split_total_people || null,
        split_person_index: orderData.split_person_index || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        // Store user information for tracking
        created_by: orderData.created_by || createdByUser,
        created_by_email: createdByEmail,
        created_by_name: createdByUser,
        // FRAUD PREVENTION #4: Link order to current shift
        created_by_shift_id: localStorage.getItem('saheeh_pos_shift_id') || '',
        branch: 'saheeh-riyadh',
        source: 'pos' // CRITICAL: Mark orders from POS so they're not filtered out
      };

      // GoBD/KassenSichV: freeze the per-rate tax snapshot at write time so
      // historical reports always reflect the rates in force when the order
      // was placed, even if helpers/rates change later. Computed from the
      // already-mapped items above which carry per-line tax_rate + tax_amount.
      const taxBreakdownByRate: Record<string, { gross: number; net: number; tax: number }> = {};
      for (const item of firebaseOrder.items) {
        const rateKey = String(item.tax_rate);
        const bucket = taxBreakdownByRate[rateKey] || { gross: 0, net: 0, tax: 0 };
        bucket.gross += item.total;
        bucket.net += item.subtotal;
        bucket.tax += item.tax_amount;
        taxBreakdownByRate[rateKey] = bucket;
      }
      const orderTaxAmount = Object.values(taxBreakdownByRate).reduce((s, b) => s + b.tax, 0);
      const orderNetAmount = Object.values(taxBreakdownByRate).reduce((s, b) => s + b.net, 0);
      firebaseOrder.tax_amount = orderTaxAmount;
      firebaseOrder.net_amount = orderNetAmount;
      firebaseOrder.tax_breakdown = {
        by_rate: taxBreakdownByRate,
        currency: 'EUR',
        // Computed at order-create time. Reports MAY recompute live, but this
        // field is the canonical snapshot for Finanzamt audits.
        snapshot_version: 1,
      };

      // Save to Firebase Firestore using SDK (handles auth automatically)
      console.log('🔥 [FIREBASE ORDER] Adding order to Firestore collection...');
      const ordersRef = collection(db, 'orders');
      const docRef = await addDoc(ordersRef, {
        ...firebaseOrder,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });

      const createdOrder: FirebaseOrder = {
        ...firebaseOrder,
        id: docRef.id,
      };

      console.log('✅ [FIREBASE ORDER] Order created successfully:', {
        id: docRef.id,
        order_number: createdOrder.order_number,
        customer: createdOrder.customer_name,
        total: createdOrder.total,
      });
      return createdOrder;

    } catch (error: any) {
      console.error('❌ [FIREBASE ORDER] Failed to create order:', error);
      console.error('❌ [FIREBASE ORDER] Error details:', {
        code: error.code,
        message: error.message,
      });
      throw error;
    }
  }

  /**
   * Get all orders from Firebase
   */
  public async getOrders(): Promise<FirebaseOrder[]> {
    try {
      console.log('🔥 [FIREBASE ORDER] Fetching orders from Firestore...');
      
      const response = await fetch(`${this.firestoreUrl}/orders`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch orders: ${response.status}`);
      }

      const data = await response.json();
      const orders: FirebaseOrder[] = [];

      if (data.documents) {
        data.documents.forEach((doc: any) => {
          const order = this.convertFromFirestoreFields(doc.fields);
          order.id = doc.name.split('/').pop();
          orders.push(order);
        });
      }

      console.log(`✅ [FIREBASE ORDER] Fetched ${orders.length} orders`);
      return orders;

    } catch (error) {
      console.error('❌ [FIREBASE ORDER] Failed to fetch orders:', error);
      throw error;
    }
  }

  /**
   * Update customer name and address for an order
   * Only updates customer_name and customer_address fields
   */
  public async updateOrderCustomerInfo(
    orderId: string,
    customerName: string,
    customerAddress?: string
  ): Promise<FirebaseOrder> {
    try {
      console.log(`🔥 [FIREBASE ORDER] Updating customer info for order ${orderId}`);
      console.log(`🔥 [FIREBASE ORDER] Customer name: ${customerName}`);
      console.log(`🔥 [FIREBASE ORDER] Customer address: ${customerAddress || 'Not provided'}`);
      
      // Use Firebase SDK for proper authentication
      const { doc, updateDoc, getDoc, serverTimestamp } = await import('../services/firestoreCompat');
      const { db, auth } = await import('./firebaseConfig');
      
      // Verify authentication
      if (!auth.currentUser) {
        console.error('❌ [FIREBASE ORDER] No authenticated user found!');
        throw new Error('User not authenticated. Please login again.');
      }
      console.log('✅ [FIREBASE ORDER] Authenticated user:', auth.currentUser.email);
      
      // Get order document reference
      const orderRef = doc(db, 'orders', orderId);
      
      // Verify order exists
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) {
        throw new Error(`Order ${orderId} not found`);
      }
      
      // Prepare update data (only customer_name and customer_address)
      const updateData: any = {
        customer_name: customerName,
        updated_at: serverTimestamp(),
      };
      
      // Only include customer_address if provided
      if (customerAddress !== undefined) {
        updateData.customer_address = customerAddress || '';
      }
      
      // Update order
      console.log(`🔥 [FIREBASE ORDER] Updating order customer info in Firestore...`);
      await updateDoc(orderRef, updateData);
      
      // Fetch updated order
      const updatedSnap = await getDoc(orderRef);
      const updatedData = updatedSnap.data();
      
      const updatedOrder: FirebaseOrder = {
        id: orderId,
        order_number: updatedData?.order_number || '',
        customer_name: updatedData?.customer_name || '',
        order_type: updatedData?.order_type || 'dine_in',
        payment_method: updatedData?.payment_method || 'cash',
        payment_status: updatedData?.payment_status || 'pending',
        status: updatedData?.status || 'pending',
        subtotal: updatedData?.subtotal || 0,
        tax: updatedData?.tax || 0,
        total: updatedData?.total || 0,
        notes: updatedData?.notes || '',
        table_number: updatedData?.table_number || null,
        items: updatedData?.items || [],
        is_split_payment: updatedData?.is_split_payment || false,
        split_session_id: updatedData?.split_session_id || null,
        split_person_name: updatedData?.split_person_name || null,
        split_total_people: updatedData?.split_total_people || null,
        split_person_index: updatedData?.split_person_index || null,
        created_at: updatedData?.created_at?.toDate?.()?.toISOString() || new Date().toISOString(),
        updated_at: updatedData?.updated_at?.toDate?.()?.toISOString() || new Date().toISOString(),
        created_by: updatedData?.created_by || '',
        branch: updatedData?.branch || '',
      };

      console.log(`✅ [FIREBASE ORDER] Order ${orderId} customer info updated successfully`);
      return updatedOrder;

    } catch (error: any) {
      console.error(`❌ [FIREBASE ORDER] Failed to update order customer info ${orderId}:`, error);
      console.error('❌ [FIREBASE ORDER] Error details:', {
        code: error.code,
        message: error.message,
      });
      throw error;
    }
  }

  /**
   * Add items to an existing order (for dine-in additional rounds)
   * Appends new items, recalculates totals
   */
  public async addItemsToOrder(orderId: string, newItems: any[], newSubtotal: number, newTax: number, newTotal: number, customerName?: string): Promise<any> {
    try {
      console.log(`🔥 [FIREBASE ORDER] Adding ${newItems.length} items to order ${orderId}`);
      const { doc, updateDoc, getDoc, serverTimestamp } = await import('../services/firestoreCompat');
      const { db, auth } = await import('./firebaseConfig');

      if (!auth.currentUser) {
        throw new Error('User not authenticated. Please login again.');
      }

      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) {
        throw new Error(`Order ${orderId} not found`);
      }

      const existingData = orderSnap.data();

      // Guard: don't allow adding to a finalized order — totals, reports and
      // kitchen tickets would desync. Client must re-open the order first.
      const blockedStatuses = ['completed', 'cancelled', 'delivered', 'refunded'];
      const currentStatus = (existingData.status || '').toLowerCase();
      if (blockedStatuses.includes(currentStatus)) {
        throw new Error(`Cannot add items to a ${currentStatus} order. Please reopen the order or create a new one.`);
      }

      const existingItems = existingData.items || [];

      // Merge items: append new items to existing
      const mergedItems = [...existingItems, ...newItems];

      // Recalculate totals
      const updatedSubtotal = parseFloat(existingData.subtotal || 0) + newSubtotal;
      const updatedTax = parseFloat(existingData.tax || 0) + newTax;
      const updatedTotal = parseFloat(existingData.total || 0) + newTotal;

      const updatePayload: any = {
        items: mergedItems,
        subtotal: updatedSubtotal,
        tax: updatedTax,
        total: updatedTotal,
        item_count: mergedItems.length,
        updated_at: serverTimestamp(),
      };

      // Update customer_name if a real name is provided and current is a system default
      const isSystemName = (n: string | undefined) =>
        !n || n === 'Laufkunde' || n === 'Walk-in Customer' || n === 'Bestellung' || n.startsWith('Dine-In');
      const existingName = existingData.customer_name || '';
      if (customerName && !isSystemName(customerName)) {
        // New name is a real person — always update
        updatePayload.customer_name = customerName;
      } else if (!isSystemName(existingName)) {
        // Existing name is a real person — keep it, don't overwrite with a default
      } else if (customerName) {
        // Both are defaults — update only if existing is empty
        if (!existingName) updatePayload.customer_name = customerName;
      }

      await updateDoc(orderRef, updatePayload);

      const finalCustomerName = updatePayload.customer_name || existingData.customer_name;
      console.log(`✅ [FIREBASE ORDER] Added ${newItems.length} items to order ${orderId}. New total: €${updatedTotal.toFixed(2)}, items: ${mergedItems.length}, customer: ${finalCustomerName}`);

      return {
        id: orderId,
        order_number: existingData.order_number,
        ...existingData,
        items: mergedItems,
        subtotal: updatedSubtotal,
        tax: updatedTax,
        total: updatedTotal,
        item_count: mergedItems.length,
        customer_name: finalCustomerName,
      };
    } catch (error) {
      console.error('❌ [FIREBASE ORDER] Error adding items to order:', error);
      throw error;
    }
  }

  /**
   * Update order status
   * Uses Firebase SDK instead of REST API to properly handle authentication
   */
  public async updateOrderStatus(orderId: string, status: FirebaseOrder['status']): Promise<FirebaseOrder> {
    try {
      console.log(`🔥 [FIREBASE ORDER] Updating order ${orderId} status to ${status}`);
      console.log(`🔥 [FIREBASE ORDER] STEP 1: Importing Firebase SDK...`);
      
      // Use Firebase SDK for proper authentication
      const { doc, updateDoc, getDoc, serverTimestamp } = await import('../services/firestoreCompat');
      const { db, auth } = await import('./firebaseConfig');
      
      // Verify authentication
      console.log(`🔥 [FIREBASE ORDER] STEP 2: Checking authentication...`);
      if (!auth.currentUser) {
        console.error('❌ [FIREBASE ORDER] No authenticated user found!');
        throw new Error('User not authenticated. Please login again.');
      }
      console.log('✅ [FIREBASE ORDER] Authenticated user:', auth.currentUser.email);
      
      // Get order document reference
      const orderRef = doc(db, 'orders', orderId);
      
      // Verify order exists
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) {
        throw new Error(`Order ${orderId} not found`);
      }
      
      // CRITICAL: If order is delivered/completed, ensure payment_status is also completed
      // and create payment record if it doesn't exist
      const currentData = orderSnap.data();
      const currentPaymentStatus = currentData?.payment_status || 'pending';
      const currentOrderStatus = currentData?.status || 'pending';
      // Check if order is delivered/completed (either current status or new status)
      const isDeliveredOrCompleted = (status === 'delivered' || status === 'completed') || 
                                     (currentOrderStatus === 'delivered' || currentOrderStatus === 'completed');
      const needsPaymentUpdate = isDeliveredOrCompleted && currentPaymentStatus !== 'completed';
      
      // Prepare update data
      const updateData: any = {
        status: status,
        updated_at: serverTimestamp(),
      };

      // FRAUD PREVENTION #15: Sealed bag protocol
      // When status changes to 'ready', stamp who packed/sealed the bag
      if (status === 'ready' && currentData?.status !== 'ready') {
        updateData.sealed_at = serverTimestamp();
        updateData.sealed_by = auth.currentUser?.email || 'unknown';
        console.log(`🔒 [SEALED BAG] Order sealed by ${updateData.sealed_by}`);
      }

      // If order is delivered/completed, update payment_status to completed
      if (needsPaymentUpdate) {
        updateData.payment_status = 'completed';
        console.log(`💳 [FIREBASE ORDER] Updating payment_status to 'completed' for delivered/completed order`);
      }
      
      // Update order status (and payment_status if needed)
      console.log(`🔥 [FIREBASE ORDER] STEP 3: Updating order in Firestore...`);
      await updateDoc(orderRef, updateData);
      
      // CRITICAL: Create payment record if order is delivered/completed and payment doesn't exist
      if ((status === 'delivered' || status === 'completed') && currentData) {
        try {
          const { collection, query, where, getDocs } = await import('../services/firestoreCompat');
          const paymentsRef = collection(db, 'payments');
          
          // Check if payment already exists for this order
          const paymentQuery = query(paymentsRef, where('order_id', '==', orderId));
          const paymentSnapshot = await getDocs(paymentQuery);
          
          if (paymentSnapshot.empty) {
            // Payment doesn't exist - create it
            console.log(`💳 [FIREBASE ORDER] Creating payment record for order ${orderId}`);
            const firebasePaymentService = (await import('./firebasePaymentService')).default;
            const paymentService = firebasePaymentService.getInstance();
            
            const paymentData = {
              order_id: orderId,
              order_number: currentData.order_number || orderId,
              customer_name: currentData.customer_name || 'Walk-in Customer',
              payment_method: currentData.payment_method || 'cash',
              amount: parseFloat(currentData.total) || 0,
              status: 'completed',
              table_number: currentData.table_number || null,
              processed_by: auth.currentUser?.email || 'POS System',
            };
            
            await paymentService.createPayment(paymentData);
            console.log(`✅ [FIREBASE ORDER] Payment record created successfully for order ${orderId}`);
          } else {
            console.log(`✅ [FIREBASE ORDER] Payment record already exists for order ${orderId}`);
          }
        } catch (paymentError) {
          console.error(`⚠️ [FIREBASE ORDER] Failed to create payment record (non-critical):`, paymentError);
          // Don't throw - order status update succeeded, payment creation is secondary
        }
      }
      
      // Fetch updated order
      const updatedSnap = await getDoc(orderRef);
      const updatedData = updatedSnap.data();
      
      const updatedOrder: FirebaseOrder = {
        id: orderId,
        order_number: updatedData?.order_number || '',
        customer_name: updatedData?.customer_name || '',
        order_type: updatedData?.order_type || 'dine_in',
        payment_method: updatedData?.payment_method || 'cash',
        payment_status: updatedData?.payment_status || 'pending',
        status: status,
        subtotal: updatedData?.subtotal || 0,
        tax: updatedData?.tax || 0,
        total: updatedData?.total || 0,
        notes: updatedData?.notes || '',
        table_number: updatedData?.table_number || null,
        items: updatedData?.items || [],
        // CRITICAL: Include split payment fields
        is_split_payment: updatedData?.is_split_payment || false,
        split_session_id: updatedData?.split_session_id || null,
        split_person_name: updatedData?.split_person_name || null,
        split_total_people: updatedData?.split_total_people || null,
        split_person_index: updatedData?.split_person_index || null,
        created_at: updatedData?.created_at?.toDate?.()?.toISOString() || new Date().toISOString(),
        updated_at: updatedData?.updated_at?.toDate?.()?.toISOString() || new Date().toISOString(),
        created_by: updatedData?.created_by || '',
        branch: updatedData?.branch || '',
      };

      // CRITICAL: If order was just marked as delivered/completed, ensure payment is completed
      // This handles cases where order was delivered before our fix was deployed
      if (updatedData && (status === 'delivered' || status === 'completed') && updatedData.payment_status !== 'completed') {
        console.log(`💳 [FIREBASE ORDER] Auto-fixing payment_status for delivered/completed order`);
        await updateDoc(orderRef, {
          payment_status: 'completed',
          updated_at: serverTimestamp(),
        });
        
        // Also create payment record if missing
        try {
          const { collection, query, where, getDocs } = await import('../services/firestoreCompat');
          const paymentsRef = collection(db, 'payments');
          const paymentQuery = query(paymentsRef, where('order_id', '==', orderId));
          const paymentSnapshot = await getDocs(paymentQuery);
          
          if (paymentSnapshot.empty && updatedData) {
            const firebasePaymentService = (await import('./firebasePaymentService')).default;
            const paymentService = firebasePaymentService.getInstance();
            
            const paymentData = {
              order_id: orderId,
              order_number: updatedData.order_number || orderId,
              customer_name: updatedData.customer_name || 'Walk-in Customer',
              payment_method: updatedData.payment_method || 'cash',
              amount: parseFloat(String(updatedData.total || 0)) || 0,
              status: 'completed',
              table_number: updatedData.table_number || null,
              processed_by: auth.currentUser?.email || 'POS System',
            };
            
            await paymentService.createPayment(paymentData);
            console.log(`✅ [FIREBASE ORDER] Payment record created for order ${orderId}`);
          }
        } catch (paymentError) {
          console.error(`⚠️ [FIREBASE ORDER] Failed to create payment record (non-critical):`, paymentError);
        }
      }
      
      console.log(`✅ [FIREBASE ORDER] Order ${orderId} updated successfully to ${status}`);
      return updatedOrder;

    } catch (error: any) {
      console.error(`❌ [FIREBASE ORDER] Failed to update order ${orderId}:`, error);
      console.error('❌ [FIREBASE ORDER] Error details:', {
        code: error.code,
        message: error.message,
      });
      throw error;
    }
  }

  // NOTE: convertToFirestoreFields removed - now using Firebase SDK which doesn't need field conversion

  /**
   * Convert Firestore fields to order data
   */
  private convertFromFirestoreFields(fields: any): FirebaseOrder {
    return {
      order_number: fields.order_number?.stringValue || '',
      customer_name: fields.customer_name?.stringValue || '',
      order_type: fields.order_type?.stringValue as any || 'dine_in',
      payment_method: fields.payment_method?.stringValue as any || 'cash',
      payment_status: fields.payment_status?.stringValue as any || 'pending',
      status: fields.status?.stringValue as any || 'pending',
      subtotal: fields.subtotal?.doubleValue || 0,
      tax: fields.tax?.doubleValue || 0,
      total: fields.total?.doubleValue || 0,
      notes: fields.notes?.stringValue || '',
      table_number: fields.table_number?.stringValue || null,
      items: fields.items?.arrayValue?.values?.map((item: any) => ({
        menu_item_id: item.mapValue.fields.menu_item_id?.stringValue || '',
        menu_item_name: item.mapValue.fields.menu_item_name?.stringValue || '',
        quantity: item.mapValue.fields.quantity?.integerValue || 0,
        unit_price: item.mapValue.fields.unit_price?.doubleValue || 0,
        subtotal: item.mapValue.fields.subtotal?.doubleValue || 0,
        tax_rate: item.mapValue.fields.tax_rate?.doubleValue || 0,
        tax_amount: item.mapValue.fields.tax_amount?.doubleValue || 0,
        total: item.mapValue.fields.total?.doubleValue || 0,
        notes: item.mapValue.fields.notes?.stringValue || ''
      })) || [],
      // CRITICAL: Include split payment fields
      is_split_payment: fields.is_split_payment?.booleanValue || false,
      split_session_id: fields.split_session_id?.stringValue || null,
      split_person_name: fields.split_person_name?.stringValue || null,
      split_total_people: fields.split_total_people?.integerValue || null,
      split_person_index: fields.split_person_index?.integerValue || null,
      created_at: fields.created_at?.stringValue || new Date().toISOString(),
      updated_at: fields.updated_at?.stringValue || new Date().toISOString(),
      created_by: fields.created_by?.stringValue || '',
      branch: fields.branch?.stringValue || ''
    };
  }
}

export default FirebaseOrderService;
